if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends HTMLElement {
    connectedCallback() {
      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
        return;
      }
      this.events?.abort(); this.events = new AbortController();
      const listen = (node, type, handler, options = {}) => node?.addEventListener(type, handler, { ...options, signal: this.events.signal });
      this.dialog = this.querySelector('[role="dialog"]');
      this.viewport = this.querySelector('.cn-media-viewport');
      this.content = this.querySelector('.product-media-modal__content');
      this.zoom = 1; this.pan = { x: 0, y: 0 };
      listen(this.querySelector('[id^="ModalClose-"]'), 'click', () => this.hide());
      listen(this, 'click', event => { if (event.target === this) this.hide(); });
      listen(this, 'keydown', event => this.onKeydown(event));
      listen(this, 'keyup', event => {
        if (!this.hasAttribute('open') || !['Escape', 'ESCAPE'].includes(event.key || event.code)) return;
        event.preventDefault(); event.stopPropagation(); this.hide();
      });
      listen(this.querySelector('[data-cn-modal-prev]'), 'click', () => this.navigate(-1));
      listen(this.querySelector('[data-cn-modal-next]'), 'click', () => this.navigate(1));
      listen(this.querySelector('[data-cn-zoom-in]'), 'click', () => this.setZoom(this.zoom + .5));
      listen(this.querySelector('[data-cn-zoom-out]'), 'click', () => this.setZoom(this.zoom - .5));
      listen(this.querySelector('[data-cn-zoom-reset]'), 'click', () => this.setZoom(1));
      this.querySelectorAll('[data-cn-pan]').forEach(button => listen(button, 'click', () => this.panByDirection(button.dataset.cnPan)));
      listen(this.querySelector('[data-cn-modal-thumbnails]'), 'click', event => {
        const button = event.target.closest('[data-cn-modal-index]');
        if (button) this.activate(Number(button.dataset.cnModalIndex));
      });
      listen(this.viewport, 'pointerdown', event => this.onPointerDown(event));
      listen(this.viewport, 'pointermove', event => this.onPointerMove(event));
      listen(this.viewport, 'pointerup', event => this.onPointerUp(event));
      listen(this.viewport, 'pointercancel', () => { this.drag = null; });
      listen(this.viewport, 'click', event => {
        if (event.target !== this.activeMedia || this.activeMedia?.tagName !== 'IMG') return;
        if (this.ignoreClick) { this.ignoreClick = false; return; }
        this.setZoom(this.zoom === 1 ? 2 : 1);
      });
      // Loaded images may have a different ratio from their provisional attributes.
      listen(this.content, 'load', event => { if (event.target === this.activeMedia) this.applyTransform(); }, { capture: true });
      this.resizeObserver?.disconnect();
      if (typeof ResizeObserver !== 'undefined' && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => { if (this.hasAttribute('open')) this.applyTransform(); });
        this.resizeObserver.observe(this.viewport);
      }
      listen(window, 'resize', () => { if (this.hasAttribute('open')) this.applyTransform(); });
    }

    disconnectedCallback() {
      if (this.hasAttribute('open')) this.hide(true);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.drag = null;
    }

    get media() { return [...(this.content?.children || [])].filter(item => item.hasAttribute('data-media-id')); }
    get activeMedia() { return this.media[this.index]; }

    show(opener) {
      if (!this.content) return;
      this.openedBy = opener;
      this.sourceGallery = opener?.closest('media-gallery');
      this.parentDialog = opener?.closest('quick-add-modal[open]');
      const index = this.media.findIndex(item => item.dataset.mediaId === opener?.dataset.mediaId);
      this.setAttribute('open', ''); document.body.classList.add('overflow-hidden');
      this.buildThumbnails(); this.activate(index >= 0 ? index : 0, false);
      if (typeof trapFocus === 'function') trapFocus(this, this.dialog); else this.dialog?.focus();
    }

    hide(preventFocus = false) {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open'); this.setZoom(1); this.drag = null;
      window.pauseAllMedia?.();
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const parent = this.parentDialog?.isConnected && this.parentDialog.hasAttribute('open') ? this.parentDialog : null;
      if (!parent && !document.querySelector('quick-add-modal[open],product-modal[open],modal-dialog[open],cart-drawer.active')) document.body.classList.remove('overflow-hidden');
      if (!preventFocus) {
        // Moving through the dialog also changes the gallery's visible slide.
        // Return to that slide instead of its now-hidden original opener.
        const activeOpener = this.sourceGallery?.isConnected
          ? this.sourceGallery.querySelector('[data-media-id].is-active modal-opener button:not([disabled])') : null;
        const originalOpener = this.openedBy?.isConnected && !this.openedBy.closest('[hidden],[inert],[aria-hidden="true"]')
          ? this.openedBy : null;
        const target = activeOpener || originalOpener || parent?.querySelector('[role="dialog"]');
        if (parent && typeof trapFocus === 'function') trapFocus(parent, target || parent);
        else target?.focus();
      }
      this.parentDialog = null;
    }

    buildThumbnails() {
      const container = this.querySelector('[data-cn-modal-thumbnails]'); if (!container) return;
      container.replaceChildren();
      this.media.forEach((media, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.cnModalIndex = index;
        button.setAttribute('aria-label', `${media.tagName === 'IMG' ? 'Bild' : 'Medium'} ${index + 1} anzeigen`);
        const source = media.tagName === 'IMG' ? media : media.querySelector('img');
        if (source) { const img = document.createElement('img'); img.src = source.getAttribute('src'); img.alt = ''; img.width = 64; img.height = 54; img.loading = 'lazy'; button.append(img); }
        else button.textContent = String(index + 1);
        container.append(button);
      });
    }

    replaceMedia(content, featuredId) {
      this.content.replaceChildren(...[...content.childNodes].map(node => node.cloneNode(true)));
      this.buildThumbnails();
      const index = this.media.findIndex(item => item.dataset.mediaId === String(featuredId));
      this.activate(index >= 0 ? index : 0, false);
      if (this.hasAttribute('open') && typeof trapFocus === 'function') trapFocus(this, this.dialog);
    }

    activate(index, sync = true) {
      const items = this.media; if (!items.length) return;
      this.index = ((index % items.length) + items.length) % items.length;
      this.zoom = 1; this.pan = { x: 0, y: 0 }; this.drag = null;
      items.forEach((item, i) => { item.classList.toggle('active', i === this.index); item.hidden = i !== this.index; item.style.removeProperty('transform'); });
      this.querySelectorAll('[data-cn-modal-index]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.cnModalIndex) === this.index)));
      const thumbnail = this.querySelector('[data-cn-modal-index][aria-current="true"]');
      const thumbnailList = thumbnail?.parentElement;
      if (thumbnailList?.clientWidth > 0) {
        if (thumbnail.offsetLeft < thumbnailList.scrollLeft) thumbnailList.scrollLeft = thumbnail.offsetLeft;
        else if (thumbnail.offsetLeft + thumbnail.offsetWidth > thumbnailList.scrollLeft + thumbnailList.clientWidth) thumbnailList.scrollLeft = thumbnail.offsetLeft + thumbnail.offsetWidth - thumbnailList.clientWidth;
      }
      this.querySelector('[data-cn-modal-position]').textContent = `${this.index + 1} / ${items.length}`;
      this.querySelector('[data-cn-modal-status]').textContent = `${this.index + 1} von ${items.length}: ${this.activeMedia.getAttribute('alt') || 'Produktansicht'}`;
      this.querySelectorAll('[data-cn-modal-prev],[data-cn-modal-next]').forEach(button => { button.disabled = items.length < 2; button.hidden = items.length < 2; });
      window.pauseAllMedia?.();
      if (this.hasAttribute('open')) this.activeMedia.loadContent?.(false);
      this.applyTransform();
      if (sync && this.sourceGallery?.isConnected) {
        const item = [...this.sourceGallery.querySelectorAll('[data-media-id]')].find(element => element.dataset.mediaId.endsWith('-' + this.activeMedia.dataset.mediaId));
        if (item) this.sourceGallery.setActiveMedia(item.dataset.mediaId, false);
      }
    }

    navigate(direction) { if (this.hasAttribute('open')) this.activate(this.index + direction); }

    setZoom(value) {
      this.zoom = this.activeMedia?.tagName === 'IMG' ? Math.min(4, Math.max(1, value)) : 1;
      if (this.zoom === 1) this.pan = { x: 0, y: 0 };
      this.applyTransform();
    }

    panByDirection(direction) {
      if (this.activeMedia?.tagName !== 'IMG' || this.zoom <= 1) return;
      const offsets = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };
      if (!offsets[direction]) return;
      const step = Math.max(40, Math.min(this.viewport.clientWidth, this.viewport.clientHeight) * .2);
      const [x, y] = offsets[direction];
      this.pan = { x: this.pan.x + x * step, y: this.pan.y + y * step };
      this.applyTransform();
    }

    applyTransform() {
      const image = this.activeMedia;
      if (!image) return;
      const imageMode = image.tagName === 'IMG';
      this.querySelector('[data-cn-zoom-out]').disabled = !imageMode || this.zoom <= 1;
      this.querySelector('[data-cn-zoom-in]').disabled = !imageMode || this.zoom >= 4;
      this.querySelector('[data-cn-zoom-reset]').disabled = !imageMode || this.zoom === 1;
      const zoomLabel = `${Math.round(this.zoom * 100)} %`;
      this.querySelector('[data-cn-zoom-label]').textContent = zoomLabel;
      this.querySelector('[data-cn-zoom-reset]').setAttribute('aria-label', `${zoomLabel} – Bild auf Originalansicht zurücksetzen`);
      this.viewport.classList.toggle('is-zoomed', imageMode && this.zoom > 1);
      const panControls = this.querySelector('[data-cn-pan-controls]');
      if (panControls) panControls.hidden = !imageMode || this.zoom <= 1;
      if (!imageMode) return;
      const width = this.viewport.clientWidth, height = this.viewport.clientHeight;
      const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || width || 1;
      const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || height || 1;
      const inset = Math.min(40, Math.max(16, Math.min(width, height) * .05));
      const ratio = Math.max(0, Math.min(1, (width - inset * 2) / naturalWidth, (height - inset * 2) / naturalHeight));
      image.style.width = `${naturalWidth * ratio}px`;
      image.style.height = `${naturalHeight * ratio}px`;
      const maxX = Math.max(0, (naturalWidth * ratio * this.zoom - width) / 2);
      const maxY = Math.max(0, (naturalHeight * ratio * this.zoom - height) / 2);
      this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x)); this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
      const atEdge = { left: this.pan.x <= -maxX, right: this.pan.x >= maxX, up: this.pan.y <= -maxY, down: this.pan.y >= maxY };
      this.querySelectorAll('[data-cn-pan]').forEach(button => button.setAttribute('aria-disabled', String(atEdge[button.dataset.cnPan])));
      image.style.transform = `translate(-50%, -50%) translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.zoom})`;
    }

    onKeydown(event) {
      if (!this.hasAttribute('open') || event.target.matches('input,textarea,select')) return;
      const key = event.key || event.code;
      if (event.target.closest('[data-cn-pan-controls]') && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
        event.preventDefault(); event.stopPropagation();
        this.panByDirection({ ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' }[key]);
        return;
      }
      if (!['Escape', 'ArrowLeft', 'ArrowRight', '+', '=', '-', '0'].includes(key)) return;
      event.preventDefault(); event.stopPropagation();
      // Close on keyup so the same Escape does not reach the restored parent dialog.
      if (key === 'Escape') return;
      if (key === 'ArrowLeft') this.navigate(-1); else if (key === 'ArrowRight') this.navigate(1); else this.setZoom(key === '0' ? 1 : this.zoom + (key === '-' ? -.5 : .5));
    }

    onPointerDown(event) {
      if (this.activeMedia?.tagName !== 'IMG' || (event.button != null && event.button !== 0)) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, panX: this.pan.x, panY: this.pan.y, moved: false };
      this.ignoreClick = false;
      if (this.zoom > 1) this.viewport.setPointerCapture?.(event.pointerId);
    }
    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) this.drag.moved = true;
      if (this.zoom > 1) { event.preventDefault(); this.pan = { x: this.drag.panX + dx, y: this.drag.panY + dy }; this.applyTransform(); }
    }
    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
      this.ignoreClick = this.drag.moved;
      if (this.zoom === 1 && event.pointerType === 'touch' && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.navigate(dx < 0 ? 1 : -1);
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      this.drag = null;
    }
  });
}
